/*
 * ScheduledSpecialsPanel — Admin › Menu widget for queuing tomorrow's
 * "Today's Special" from tonight. The scheduler backend flips the
 * `is_special` flag at :01 past every hour (see backend/scheduler.py) so
 * an owner can lock next week's rotation in on Sunday evening and forget it.
 *
 * If the owner schedules for TODAY, backend applies immediately.
 */
import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { CalendarPlus, Sparkles, Loader2, Trash2, Clock, CheckCircle2, Play } from "lucide-react";
import { menuHighlights } from "../../data/mock";
import SpecialsTemplatesPanel from "./SpecialsTemplatesPanel";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ScheduledSpecialsPanel({ authHeaders, toast }) {
  const [items, setItems] = useState([]);
  const [today, setToday] = useState("");
  const [dishId, setDishId] = useState("");
  const [scheduledDate, setScheduledDate] = useState("");
  const [dbDishes, setDbDishes] = useState([]); // extras from menu_items (id >= 1000)
  const [busy, setBusy] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [applying, setApplying] = useState(false);

  const load = async () => {
    setBusy(true);
    try {
      const [sched, menu] = await Promise.all([
        axios.get(`${API}/admin/menu/specials/schedule`, { headers: authHeaders }),
        axios.get(`${API}/admin/menu`, { headers: authHeaders }),
      ]);
      setItems(sched.data.items || []);
      setToday(sched.data.today || "");
      setDbDishes(
        (menu.data.items || [])
          .filter((m) => m.id >= 1000 && m.name)
          .map((m) => ({ id: m.id, name: m.name }))
      );
    } catch (e) {
      toast({ title: "Load failed", description: e?.response?.data?.detail || e.message });
    } finally {
      setBusy(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const allDishes = useMemo(() => {
    return [
      ...menuHighlights.map((m) => ({ id: m.id, name: m.name })),
      ...dbDishes,
    ].sort((a, b) => a.name.localeCompare(b.name));
  }, [dbDishes]);

  const dishNameById = useMemo(() => {
    const map = new Map();
    menuHighlights.forEach((d) => map.set(d.id, d.name));
    dbDishes.forEach((d) => map.set(d.id, d.name));
    return map;
  }, [dbDishes]);

  // 7-day rotation preview — today (Brisbane) + next 6 days. Uses the same
  // `today` string returned by the backend so DST-drift can never desync.
  const week = useMemo(() => {
    if (!today) return [];
    // Parse YYYY-MM-DD as a naive local date to avoid timezone offset math.
    const [y, m, d] = today.split("-").map(Number);
    const base = new Date(y, m - 1, d);
    // Group pending schedules by date
    const byDate = new Map();
    items.forEach((s) => {
      if (s.status === "pending") byDate.set(s.scheduled_date, s);
    });
    const days = [];
    for (let i = 0; i < 7; i++) {
      const dt = new Date(base);
      dt.setDate(base.getDate() + i);
      const iso = dt.toISOString().slice(0, 10);
      days.push({
        iso,
        weekday: dt.toLocaleDateString("en-AU", { weekday: "short" }),
        dayNum: dt.getDate(),
        isToday: i === 0,
        schedule: byDate.get(iso) || null,
      });
    }
    return days;
  }, [today, items]);

  const minDate = today || new Date().toISOString().slice(0, 10);

  const submit = async () => {
    if (!dishId || !scheduledDate) {
      toast({ title: "Missing fields", description: "Pick a dish and a date." });
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await axios.post(
        `${API}/admin/menu/specials/schedule`,
        { menu_item_id: Number(dishId), scheduled_date: scheduledDate },
        { headers: authHeaders }
      );
      const applied = data.item?.status === "applied";
      toast({
        title: applied ? "Special applied now" : "Special scheduled",
        description: applied
          ? "It's already showing on the home page."
          : `The gold strip will flip on ${scheduledDate}.`,
      });
      setDishId("");
      setScheduledDate("");
      load();
    } catch (e) {
      toast({ title: "Schedule failed", description: e?.response?.data?.detail || e.message });
    } finally {
      setSubmitting(false);
    }
  };

  const cancel = async (schedule) => {
    const displayName = dishNameById.get(schedule.menu_item_id) || schedule.dish_name;
    if (!window.confirm(`Cancel scheduled special "${displayName}" on ${schedule.scheduled_date}?`)) return;
    try {
      await axios.delete(`${API}/admin/menu/specials/schedule/${schedule.id}`, { headers: authHeaders });
      toast({ title: "Cancelled" });
      load();
    } catch (e) {
      toast({ title: "Cancel failed", description: e?.response?.data?.detail || e.message });
    }
  };

  const runNow = async () => {
    setApplying(true);
    try {
      const { data } = await axios.post(`${API}/admin/menu/specials/apply`, {}, { headers: authHeaders });
      if (data.applied > 0) {
        toast({ title: "Special applied", description: "Today's schedule is now live on the site." });
      } else {
        toast({ title: "Nothing to apply", description: `No pending special for today (${data.today}).` });
      }
      load();
    } catch (e) {
      toast({ title: "Apply failed", description: e?.response?.data?.detail || e.message });
    } finally {
      setApplying(false);
    }
  };

  return (
    <div className="p-5 border border-[var(--mm-line)] bg-[var(--mm-bg-alt)]" data-testid="scheduled-specials-panel">
      <div className="flex flex-col md:flex-row md:items-center gap-4 mb-5">
        <div className="flex-1">
          <div className="section-label mb-2 flex items-center gap-2"><Sparkles size={14} /> Scheduled Specials</div>
          <p className="text-sm text-[var(--mm-cream-muted)]">
            Queue tomorrow&apos;s Today&apos;s Special from tonight. The hourly scheduler flips it automatically at midnight Brisbane time.
            Same-day scheduling applies immediately.
          </p>
        </div>
        <button
          onClick={runNow}
          disabled={applying}
          data-testid="specials-run-now"
          className="btn-outline inline-flex items-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
          title="Force the scheduler to run — useful if you just added today's special"
        >
          {applying ? <Loader2 size={14} className="animate-spin" /> : <Play size={14} />}
          Run scheduler now
        </button>
      </div>

      {/* Form */}
      <div className="grid md:grid-cols-3 gap-3 mb-5">
        <div>
          <label className="block text-[0.62rem] tracking-[0.22em] uppercase text-[var(--mm-cream-muted)] mb-2">Dish</label>
          <select
            value={dishId}
            onChange={(e) => setDishId(e.target.value)}
            data-testid="specials-dish-select"
            className="w-full bg-[var(--mm-bg)] border border-[var(--mm-line)] text-[var(--mm-cream)] px-3 py-2 text-sm focus:border-[var(--mm-accent)] outline-none"
          >
            <option value="">Pick a dish…</option>
            {allDishes.map((d) => (
              <option key={d.id} value={d.id}>{d.name}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-[0.62rem] tracking-[0.22em] uppercase text-[var(--mm-cream-muted)] mb-2">Feature on</label>
          <input
            type="date"
            value={scheduledDate}
            min={minDate}
            onChange={(e) => setScheduledDate(e.target.value)}
            data-testid="specials-date-input"
            className="w-full bg-transparent border border-[var(--mm-line)] text-[var(--mm-cream)] px-3 py-2 text-sm focus:border-[var(--mm-accent)] outline-none"
            style={{ colorScheme: "dark" }}
          />
        </div>
        <div className="flex items-end">
          <button
            onClick={submit}
            disabled={submitting || !dishId || !scheduledDate}
            data-testid="specials-submit"
            className="w-full btn-primary inline-flex items-center justify-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {submitting ? <Loader2 size={14} className="animate-spin" /> : <CalendarPlus size={14} />}
            Schedule
          </button>
        </div>
      </div>

      {/* 7-day rotation preview */}
      {week.length > 0 && (
        <div className="mb-5" data-testid="specials-week-preview">
          <div className="text-[0.6rem] tracking-[0.22em] uppercase text-[var(--mm-cream-muted)] mb-2">Next 7 days</div>
          <div className="grid grid-cols-7 gap-1.5">
            {week.map((day) => {
              const name = day.schedule ? (dishNameById.get(day.schedule.menu_item_id) || day.schedule.dish_name) : null;
              return (
                <button
                  key={day.iso}
                  onClick={() => setScheduledDate(day.iso)}
                  data-testid={`week-day-${day.iso}`}
                  data-scheduled={name ? "true" : "false"}
                  title={name ? `${name} — click to edit` : "Click to schedule this day"}
                  className={`
                    text-left p-2.5 border transition-colors min-h-[68px] flex flex-col justify-between
                    ${day.isToday ? "border-[var(--mm-accent)] bg-[var(--mm-accent)]/5" : "border-[var(--mm-line)] bg-[var(--mm-bg)]"}
                    ${name ? "hover:border-[var(--mm-accent)]" : "hover:border-[var(--mm-cream-muted)]"}
                  `}
                >
                  <div className="flex items-baseline justify-between">
                    <span className="text-[0.55rem] tracking-[0.24em] uppercase text-[var(--mm-cream-muted)]">
                      {day.weekday}
                    </span>
                    <span className={`font-display italic text-lg ${day.isToday ? "text-[var(--mm-accent)]" : "text-[var(--mm-cream)]"}`}>
                      {day.dayNum}
                    </span>
                  </div>
                  <div className={`text-[0.65rem] leading-tight line-clamp-2 mt-1.5 ${name ? "text-[var(--mm-cream)]" : "text-[var(--mm-cream-muted)]/50"}`}>
                    {name || "—"}
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Queue */}
      {busy ? (
        <div className="py-8 text-center"><Loader2 size={22} className="mx-auto text-[var(--mm-accent)] animate-spin" /></div>
      ) : items.length === 0 ? (
        <div className="p-6 text-center border border-dashed border-[var(--mm-line)] text-[var(--mm-cream-muted)] text-sm">
          Nothing queued. Pick a dish + date above.
        </div>
      ) : (
        <ul className="space-y-2" data-testid="specials-queue">
          {items.map((s) => (
            <li key={s.id} data-testid={`specials-queue-${s.id}`} className="p-3 border border-[var(--mm-line)] bg-[var(--mm-bg)] flex flex-col md:flex-row md:items-center md:justify-between gap-3">
              <div className="flex items-center gap-3">
                <Clock size={13} className="text-[var(--mm-accent)] shrink-0" />
                <div>
                  <div className="font-display italic text-lg text-[var(--mm-cream)]">{dishNameById.get(s.menu_item_id) || s.dish_name}</div>
                  <div className="text-[0.62rem] tracking-[0.22em] uppercase text-[var(--mm-cream-muted)]">
                    {s.scheduled_date}{s.scheduled_date === today ? " · TODAY" : ""}
                  </div>
                </div>
              </div>
              <button
                onClick={() => cancel(s)}
                data-testid={`specials-cancel-${s.id}`}
                className="text-[0.62rem] tracking-[0.22em] uppercase text-[var(--mm-cream-muted)] hover:text-red-400 inline-flex items-center gap-1"
              >
                <Trash2 size={11} /> Cancel
              </button>
            </li>
          ))}
        </ul>
      )}

      {/* Weekly Templates — save & re-apply Mon-Fri combos */}
      <SpecialsTemplatesPanel
        authHeaders={authHeaders}
        toast={toast}
        dishOptions={allDishes}
        dishNameById={dishNameById}
        onApplied={load}
      />
    </div>
  );
}
